import { isAbortError } from "../errors";
import { LoggerService } from "../services/LoggerService";

import {
  type RobotoServiceErrorBody,
  RobotoDomainException,
  RobotoServiceException,
} from "./exceptions";
import { truncate, UnexpectedResponseError } from "./UnexpectedResponseError";

interface RobotoServiceSuccessResponse<T> {
  data: T;
}

function isSuccessResponse(
  response: unknown,
): response is RobotoServiceSuccessResponse<unknown> {
  return (
    typeof response === "object" && response !== null && "data" in response
  );
}

interface RobotoServiceErrorResponse<T> {
  error: T;
}

function isErrorResponse(
  response: unknown,
): response is RobotoServiceErrorResponse<unknown> {
  return (
    typeof response === "object" && response !== null && "error" in response
  );
}

function isJsonObject(response: unknown): response is JsonObject {
  return typeof response === "object" && response !== null;
}

interface JsonReviver {
  (key: string, value: unknown, context?: { source: string }): unknown;
}

/**
 * Why a 2XX body could not be read as a Roboto API envelope, phrased as a noun
 * phrase completing "...returned <reason>". This is the only account anyone
 * gets of a response that reached the client intact but means nothing to it, so
 * it names what the body actually was rather than what it wasn't.
 */
function describeUnexpectedBody(
  body: unknown,
  bodyText: string,
  parseError: unknown,
  contentType: string | null,
): string {
  if (bodyText.trim() === "") {
    return "an empty body, not the expected { data: ... } envelope";
  }

  if (parseError !== undefined) {
    const declaredType = contentType ?? "no content-type";
    return `a body that is not JSON (${declaredType}): "${truncate(bodyText)}"`;
  }

  if (Array.isArray(body)) {
    return "a JSON array, not the expected { data: ... } envelope";
  }

  if (isJsonObject(body)) {
    const keys = Object.keys(body).join(", ");
    return `a JSON object with keys [${keys}], not the expected { data: ... } envelope`;
  }

  return `the JSON value ${truncate(bodyText)}, not the expected { data: ... } envelope`;
}

export class HttpResponse {
  #response: Response;

  constructor(response: Response) {
    this.#response = response;
  }

  public get raw(): Response {
    return this.#response;
  }

  public get ok(): boolean {
    return this.#response.ok;
  }

  public get headers(): Headers {
    return this.#response.headers;
  }

  public get status(): number {
    return this.#response.status;
  }

  public arrayBuffer(): Promise<ArrayBuffer> {
    return this.#response.arrayBuffer();
  }

  /**
   * The response body, unwrapped from the `{ data: ... }` envelope the Roboto
   * API returns.
   *
   * @throws {RobotoDomainException} If the response carries an error, either as
   * an error envelope or as a non-2XX status this client can map.
   * @throws {UnexpectedResponseError} If the response is a 2XX whose body is
   * not an envelope — which means it did not come from an API route handler.
   * @throws The underlying failure, verbatim, if the body could not be read at
   * all: an abort, or a connection dropped after the status line arrived.
   */
  public json<ResponseBodyType>(
    reviver?: JsonReviver,
  ): Promise<ResponseBodyType> {
    return this.jsonParsedBy<ResponseBodyType>((text) =>
      JSON.parse(text, reviver),
    );
  }

  /**
   * `json`, with the body's text parsed by `parse` rather than `JSON.parse`,
   * for a parser that returns what `JSON.parse` would with a reviver, faster.
   * It throws as `json` does.
   */
  public async jsonParsedBy<ResponseBodyType>(
    parse: (text: string) => unknown,
  ): Promise<ResponseBodyType> {
    // Read as text rather than `Response.json()`: `parse` reads the text, and
    // the raw text is what makes a body that isn't JSON reportable below.
    let bodyText = "";
    try {
      bodyText = await this.#response.text();
    } catch (e) {
      // The status line arrived but the body did not — an aborted read, a
      // dropped connection, a truncated response. On a 2XX there is nothing
      // else to report, and the transport error describes the failure better
      // than this layer can, so it goes up as itself. On an error response the
      // status still means something, so fall through and map it.
      if (isAbortError(e) || this.#response.ok) {
        if (!isAbortError(e)) {
          // The rethrown error names the transport failure but not the request
          // it belongs to, and an abort is routine enough that logging every
          // one would bury the rest.
          LoggerService.error(
            `Failed to read the body of HTTP ${this.#response.status} from ${this.#response.url}`,
            e,
          );
        }
        throw e;
      }
    }

    let responseBody:
      | RobotoServiceSuccessResponse<ResponseBodyType>
      | RobotoServiceErrorResponse<RobotoServiceErrorBody>
      | JsonValue = null;
    let parseError: unknown = undefined;
    if (bodyText.trim() !== "") {
      try {
        responseBody = parse(bodyText) as
          | RobotoServiceSuccessResponse<ResponseBodyType>
          | RobotoServiceErrorResponse<RobotoServiceErrorBody>
          | JsonValue;
      } catch (e) {
        // Held rather than thrown: an error response is still worth mapping by
        // status, and a 2XX one is described in full by the throw at the end.
        parseError = e;
      }
    }

    if (isSuccessResponse(responseBody)) {
      return responseBody.data;
    }

    if (!this.#response.ok) {
      if (isErrorResponse(responseBody)) {
        // A well-formatted RobotoServiceErrorResponse
        throw RobotoDomainException.fromHttpResponse(responseBody.error);
      } else if (isJsonObject(responseBody) && "message" in responseBody) {
        // An error response that is not wrapped in { error: ... }
        // May be returned by the Roboto Authorizer
        const errorResponse: RobotoServiceErrorBody = {
          error_code: String(this.#response.status),
          message: String(responseBody.message),
        };
        throw RobotoDomainException.fromHttpResponse(errorResponse);
      }
      throw RobotoDomainException.fromStatusCode(this.#response.status);
    }

    const unexpectedResponse = new UnexpectedResponseError(
      this.#response,
      describeUnexpectedBody(
        responseBody,
        bodyText,
        parseError,
        this.#response.headers.get("content-type"),
      ),
      bodyText,
      { cause: parseError },
    );
    // Logged here rather than left to the catcher: the thrown message is kept
    // free of response detail because error surfaces render it, so this is the
    // only place the diagnosis is written down in full.
    LoggerService.error(unexpectedResponse.detail, unexpectedResponse);
    throw unexpectedResponse;
  }

  public text(): Promise<string> {
    return this.#response.text();
  }

  public async throwIfError(): Promise<void> {
    if (this.#response.ok === false) {
      await this.json<RobotoServiceException>();
    }
  }
}
